// Generic messages every game built on netcore speaks: welcome, join/leave
// events, server-measured ping, the scored roster, nicknames, resume tokens
// for reconnects and live handovers, and the close codes that tell a client
// what to do next. All little-endian; encoders write into caller-provided
// DataViews so a server can reuse one buffer per room and never allocate on
// the tick path.
//
// Message ids reserved by netcore: 2, 4, 5, 6, 7, 10, 13 and 32-63. A game
// defines its own ids outside that set (inputs, snapshots, shots, ...).

export const LE = true;

export const MSG_WELCOME = 2; // server -> client
export const MSG_EVENT = 4; // server -> client, for the HUD feed
export const MSG_PING = 5; // server -> client, echoed back as MSG_PONG
export const MSG_PONG = 6; // client -> server
export const MSG_SCORES = 7; // server -> client, roster for the scoreboard
export const MSG_SETNAME = 10; // client -> server, nickname (utf-8, <= NAME_MAX bytes)
export const MSG_RESUME = 13; // server -> client: the token to reconnect with

// WebSocket close codes (4000-4999 are application-defined).
export const CLOSE_UPDATED = 4100; // this server is retiring for an update: reload and rejoin
export const CLOSE_PROTOCOL = 4101; // client/server protocol mismatch: reload
export const CLOSE_RESUME = 4102; // live handover: reconnect at once with the resume token (MSG_RESUME)

// Event kinds carried by MSG_EVENT that netcore itself emits; games add
// their own from 16 upward (Mayday's EV_KILL = 3 predates the rule).
export const EV_JOIN = 1; // id = player
export const EV_LEAVE = 2; // id = player
export const EV_UPDATE_SOON = 4; // the server will retire after this match (deploy in progress)

export const NAME_MAX = 16; // bytes of utf-8

// ---- WELCOME: u8 type | u8 yourId | u16 tickRate | u8 roomIdLen | roomId (ascii)
// The room id is what the client puts in the URL and in invite links.
export const ROOM_ID_MAX = 16;
export const WELCOME_SIZE = 1 + 1 + 2 + 1 + ROOM_ID_MAX; // upper bound

export function encodeWelcome(view: DataView, id: number, tickRate: number, roomId: string): number {
  view.setUint8(0, MSG_WELCOME);
  view.setUint8(1, id);
  view.setUint16(2, tickRate, LE);
  const n = Math.min(ROOM_ID_MAX, roomId.length);
  view.setUint8(4, n);
  for (let i = 0; i < n; i++) view.setUint8(5 + i, roomId.charCodeAt(i) & 0x7f);
  return 5 + n;
}

export interface WelcomeMsg {
  id: number;
  tickRate: number;
  roomId: string;
}

export function decodeWelcome(view: DataView, out: WelcomeMsg): boolean {
  if (view.byteLength < 5) return false;
  out.id = view.getUint8(1);
  out.tickRate = view.getUint16(2, LE);
  const n = view.getUint8(4);
  if (view.byteLength < 5 + n) return false;
  let s = '';
  for (let i = 0; i < n; i++) s += String.fromCharCode(view.getUint8(5 + i));
  out.roomId = s;
  return true;
}

// ---- EVENT: u8 type | u8 kind | u8 id | u8 id2 ----------------------------
export const EVENT_SIZE = 1 + 1 + 1 + 1;

export function encodeEvent(view: DataView, kind: number, id: number, id2 = 0): number {
  view.setUint8(0, MSG_EVENT);
  view.setUint8(1, kind);
  view.setUint8(2, id);
  view.setUint8(3, id2);
  return EVENT_SIZE;
}

export interface EventMsg {
  kind: number;
  id: number;
  id2: number;
}

export function decodeEvent(view: DataView, out: EventMsg): boolean {
  if (view.byteLength < EVENT_SIZE) return false;
  out.kind = view.getUint8(1);
  out.id = view.getUint8(2);
  out.id2 = view.getUint8(3);
  return true;
}

// ---- PING / PONG: u8 type | u32 stamp ------------------------------------
// The server stamps its own clock (low 32 bits of ms); the client echoes the
// stamp unchanged, so the server measures RTT without trusting the client.
export const PING_SIZE = 1 + 4;

export function encodePing(view: DataView, type: number, stamp: number): number {
  view.setUint8(0, type);
  view.setUint32(1, stamp >>> 0, LE);
  return PING_SIZE;
}

export function decodePingStamp(view: DataView): number {
  return view.byteLength >= PING_SIZE ? view.getUint32(1, LE) : -1;
}

// ---- SETNAME: u8 type | u8 len | len bytes of utf-8 -----------------------
export const SETNAME_MAX_SIZE = 2 + NAME_MAX;

// `name` is pre-encoded utf-8 (the client encodes once when the setting
// changes). Truncated to NAME_MAX bytes.
export function encodeSetName(view: DataView, name: Uint8Array): number {
  const len = name.length > NAME_MAX ? NAME_MAX : name.length;
  view.setUint8(0, MSG_SETNAME);
  view.setUint8(1, len);
  for (let i = 0; i < len; i++) view.setUint8(2 + i, name[i]!);
  return 2 + len;
}

// Copies the name bytes into `out` (NAME_MAX bytes); returns the length, or
// -1 if malformed.
export function decodeSetName(view: DataView, out: Uint8Array): number {
  if (view.byteLength < 2) return -1;
  const len = view.getUint8(1);
  if (len > NAME_MAX || view.byteLength < 2 + len) return -1;
  for (let i = 0; i < len; i++) out[i] = view.getUint8(2 + i);
  return len;
}

// ---- SCORES: u8 type | u8 count | count * entry ---------------------------
// entry: u8 id | u8 team | u16 kills | u16 deaths | u16 ping | u8 nameLen | name
// Entries are variable length because of the name; read them in order.
export const SCORES_HEADER_SIZE = 1 + 1;
export const SCORES_ENTRY_FIXED = 1 + 1 + 2 + 2 + 2 + 1 + 1; // id team kills deaths ping flags nameLen
export const SCORE_FLAG_BOT = 1; // the slot is a server-driven bot

export function scoresMaxSize(maxPlayers: number): number {
  return SCORES_HEADER_SIZE + maxPlayers * (SCORES_ENTRY_FIXED + NAME_MAX);
}

export function writeScoresHeader(view: DataView, count: number): number {
  view.setUint8(0, MSG_SCORES);
  view.setUint8(1, count);
  return SCORES_HEADER_SIZE;
}

export function patchScoresCount(view: DataView, count: number): void {
  view.setUint8(1, count);
}

// `names` holds every player's utf-8 name at [id * NAME_MAX], `nameLen[id]`
// bytes long: the server's preallocated name table.
export function writeScoresEntry(
  view: DataView,
  offset: number,
  id: number,
  team: number,
  kills: number,
  deaths: number,
  ping: number,
  names: Uint8Array,
  nameLen: number,
  flags = 0,
): number {
  view.setUint8(offset, id);
  view.setUint8(offset + 1, team);
  view.setUint16(offset + 2, kills, LE);
  view.setUint16(offset + 4, deaths, LE);
  view.setUint16(offset + 6, ping > 65535 ? 65535 : ping, LE);
  view.setUint8(offset + 8, flags);
  view.setUint8(offset + 9, nameLen);
  const base = id * NAME_MAX;
  for (let i = 0; i < nameLen; i++) view.setUint8(offset + 10 + i, names[base + i]!);
  return offset + SCORES_ENTRY_FIXED + nameLen;
}

export interface ScoreEntry {
  id: number;
  team: number;
  kills: number;
  deaths: number;
  ping: number;
  flags: number; // SCORE_FLAG_*
  name: string;
}

// Returns the entry count, or -1 if the header is malformed.
export function readScoresCount(view: DataView): number {
  return view.byteLength >= SCORES_HEADER_SIZE ? view.getUint8(1) : -1;
}

const utf8 = new TextDecoder();

// Reads the entry at `offset` into `out`; returns the next offset, or -1 if
// the buffer ends early. The first entry starts at SCORES_HEADER_SIZE.
export function readScoresEntry(view: DataView, offset: number, out: ScoreEntry): number {
  if (view.byteLength < offset + SCORES_ENTRY_FIXED) return -1;
  out.id = view.getUint8(offset);
  out.team = view.getUint8(offset + 1);
  out.kills = view.getUint16(offset + 2, LE);
  out.deaths = view.getUint16(offset + 4, LE);
  out.ping = view.getUint16(offset + 6, LE);
  out.flags = view.getUint8(offset + 8);
  const len = view.getUint8(offset + 9);
  if (view.byteLength < offset + SCORES_ENTRY_FIXED + len) return -1;
  out.name = utf8.decode(new Uint8Array(view.buffer, view.byteOffset + offset + 10, len));
  return offset + SCORES_ENTRY_FIXED + len;
}

// ---- RESUME: u8 type | 16 bytes token ------------------------------------
// The new release recognises the token (`/ws?v=N&resume=<hex>`) and puts the
// player back into their restored slot, no spawn, no score reset.
export const RESUME_TOKEN_BYTES = 16;
export const RESUME_SIZE = 1 + RESUME_TOKEN_BYTES;

export function encodeResume(view: DataView, token: Uint8Array, offset: number): number {
  view.setUint8(0, MSG_RESUME);
  for (let i = 0; i < RESUME_TOKEN_BYTES; i++) view.setUint8(1 + i, token[offset + i]!);
  return RESUME_SIZE;
}

// Returns the token as lowercase hex, or null if malformed.
export function decodeResume(view: DataView): string | null {
  if (view.byteLength < RESUME_SIZE) return null;
  let hex = '';
  for (let i = 0; i < RESUME_TOKEN_BYTES; i++) hex += view.getUint8(1 + i).toString(16).padStart(2, '0');
  return hex;
}

// FNV-1a over a string's UTF-16 code units: fast, dependency-free, enough
// to tell two versions of a level file apart.
export function fnv1a32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
